import _ from "lodash";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useStorageState} from "react-storage-hooks";
import "../../../styles/components/anlageArbeitsListe.css";
import AnlageArbeitsListeTable from "./AnlageArbeitsListeTable";
import AnlageArbeitsListeSuche from "./AnlageArbeitsListeSuche";
import {toBase64QueryParam} from "../../../utils/URLUtils";
import {getDefaultPageInfo, toQueryParams} from "../../../utils/PageInfoUtils";
import {getAnlagenSucheEndpoint} from "../../../services/AnlagenService";
import {handleApiException, useHttp} from "../../../services/HttpService";
import {useForm} from "../../../utils/FormUtils";
import usePersonalisationAndPagination from "../usePersonalisationAndPagination";
import {MISCHWASSER, NIEDERSCHLAGSWASSER} from "../../../consts/anlagenart";

const defaultSearchParameters = {
    indirekteinleiter: {
        id: 1,
        value: "Alle Anlagen",
    },
    anlagenstatus: {
        id: "IN_BETRIEB",
        value: "in Betrieb",
    },
    anlagentyp: {
        id: "ABWASSERBEHANDLUNG",
        value: "Abwasserbehandlung"
    },
    pageInfo: getDefaultPageInfo("bezeichnung")
};

function AnlageArbeitsListe({
                                history,
                                sharedFormFactory,
                                sharedFilter,
                                onErweiterteSucheOpenChanged,
                                regionalstelleOptionen,
                                raeumlichesKriteriumOptionen,
                                behoerdeOptionen,
                                resetSharedFilter,
                            }) {
    const id = "AnlageArbeitsliste";
    const attr = {
        organisation: "organisation",
        verwaltungseinheit: "verwaltungseinheit",
        wasserkoerper: "wasserkoerper",
        anlagenBezeichnung: "anlagenBezeichnung",
        anlagentyp: "anlagentyp",
        anlagenstatus: "anlagenstatus",
        anlagenart: "anlagenart",
        entlastungstyp: "entlastungstyp",
        anlagenId: "anlagenId",

        betrieb: "betrieb",
        betriebTyp: "betriebTyp",

        indirekteinleiter: "indirekteinleiter",

        regionalstelle: "regionalstelle",
        aktenzeichen: "aktenzeichen",
        bescheidsdatum: "bescheidsdatum",
        behoerde: "behoerde",

        gueltigVon: "gueltigVon",
        gueltigBis: "gueltigBis",

        abwasserbeseitigungspflichtiger: "abwasserbeseitigungspflichtiger"
    };

    const [selected, setSelected] = useStorageState(sessionStorage, "eab-selected-anlagen", []);
    const [storedFilter, setStoredFilter] = useStorageState(sessionStorage, "eab-anlagen-filter", defaultSearchParameters);

    const {values: filter, setValues: setFilter, formFactory} = useForm({initialValues: storedFilter});

    const createQuery = useCallback((filterObject) => {
        const changeFieldValueIfPresent = (toChange, fieldName, innerFieldName) => {
            if (!toChange[fieldName]) {
                return;
            }
            toChange[fieldName] = toChange[fieldName][innerFieldName];
        };

        let f = _.cloneDeep(filterObject);
        if (!f.extendedSearchOpen) {
            delete f.raeumlich;
            delete f.gueltigVon;
            delete f.gueltigBis;
            delete f.regionalstelle;
            delete f.aktenzeichen;
            delete f.bescheidsdatum;
            delete f.behoerde;
            delete f.entlastungstyp;
            delete f.anlagenart;
            delete f.indirekteinleiter;
            delete f.abwasserbeseitigungspflichtiger;
        }

        changeFieldValueIfPresent(f, attr.anlagentyp, "id");
        changeFieldValueIfPresent(f, attr.anlagenart, "id");
        changeFieldValueIfPresent(f, attr.entlastungstyp, "id");
        changeFieldValueIfPresent(f, attr.anlagenstatus, "id");
        changeFieldValueIfPresent(f, attr.betriebTyp, "id");
        changeFieldValueIfPresent(f, attr.organisation, "id");
        changeFieldValueIfPresent(f, attr.regionalstelle, "id");
        changeFieldValueIfPresent(f, attr.behoerde, "id");
        changeFieldValueIfPresent(f, attr.abwasserbeseitigungspflichtiger, "id");

        if (f.raeumlich) {
            f[attr.verwaltungseinheit] = f["raeumlich"]["verwaltungseinheit"];
            f[attr.wasserkoerper] = f["raeumlich"]["wasserkoerper"];
            delete f.raeumlich;
        }

        if (f.indirekteinleiter) {
            switch (f.indirekteinleiter.id) {
                case 2:
                    f.indirekteinleiter = true;
                    break;
                case 3:
                    f.indirekteinleiter = false;
                    break;
                default:
                    delete f.indirekteinleiter;
                    break;
            }
        }

        if (f.anlagenart) {
            if (f.anlagenart === MISCHWASSER || f.anlagenart === NIEDERSCHLAGSWASSER) {
                f.entlastungsanlageart = f.anlagenart;
                delete f.anlagenart;
            }
        }

        if (!f.pageInfo) {
            f.pageInfo = getDefaultPageInfo("bezeichnung");
        }

        let pageInfoParams = toQueryParams(f.pageInfo);
        delete f.pageInfo;

        delete f.extendedSearchOpen;
        if (Object.keys(f).length === 0) {
            return pageInfoParams;
        }

        return "q=" + toBase64QueryParam(f) + "&" + pageInfoParams;
    }, [attr.abwasserbeseitigungspflichtiger, attr.anlagenart, attr.anlagenstatus, attr.anlagentyp, attr.behoerde, attr.betriebTyp,
        attr.entlastungstyp, attr.organisation, attr.regionalstelle, attr.verwaltungseinheit, attr.wasserkoerper]);

    const initialQuery = useRef(getAnlagenSucheEndpoint(createQuery({...storedFilter, ...sharedFilter})));
    const [{data, loading}, refetchAnlangen] = useHttp(initialQuery.current, {manual: true});
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (data && data.length > 0) {
            setSelected((prevSelected) => prevSelected.filter((selId) => data.find((dItem) => dItem.id === selId)));
        }
    }, [data, setSelected]);

    const executeSearch = (storedFilterParam, sharedFilterParam) => {
        if (initialized) {
            refetchAnlangen(getAnlagenSucheEndpoint(createQuery({...storedFilterParam, ...sharedFilterParam}))).catch((e) =>
                handleApiException(e, "Error loading Anlagearbeitsliste")
            );
        }
        setInitialized(true);
    };

    usePersonalisationAndPagination(sharedFilter, storedFilter, executeSearch);

    return (
        <>
            <AnlageArbeitsListeSuche
                formFactory={formFactory}
                attr={attr}
                sharedFilter={sharedFilter}
                onErweiterteSucheOpenChanged={onErweiterteSucheOpenChanged}
                sharedFormFactory={sharedFormFactory}
                regionalstelleOptionen={regionalstelleOptionen}
                raeumlichesKriteriumOptionen={raeumlichesKriteriumOptionen}
                behoerdeOptionen={behoerdeOptionen}
                setStoredFilter={setStoredFilter}
                setFilter={setFilter}
                resetSharedFilter={resetSharedFilter}
                executeSearch={executeSearch}
                storedFilter={storedFilter}
                setSelected={setSelected}
                filter={filter}
                id={id}
                defaultSearchParameters={defaultSearchParameters}
            />
            <AnlageArbeitsListeTable
                id={id}
                loading={loading}
                setSelected={setSelected}
                data={data}
                history={history}
                storedFilter={storedFilter}
                setStoredFilter={setStoredFilter}
                selected={selected}
                sharedFilter={sharedFilter}
                createQuery={createQuery}
            />
        </>
    );
}

export default AnlageArbeitsListe;
